import React, { useEffect, useMemo, useState } from 'react'
import { Check, Loader2, RotateCcw, Save, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useUiOverlayStore from '../../../store/useUiOverlayStore'
import { getTierStyle } from '../utils/achievementFormat'

const AchievementShowcaseEditor = ({ isOpen, achievements, initialCodes, isSaving, onClose, onSave, onReset }) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState([])

  const earned = useMemo(() => achievements.filter((achievement) => achievement.earned), [achievements])

  useEffect(() => {
    if (isOpen) setSelected(initialCodes)
  }, [initialCodes, isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const { pushOverlay, popOverlay } = useUiOverlayStore.getState()
    pushOverlay()
    document.body.style.overflow = 'hidden'
    const handleKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => {
      popOverlay()
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const toggle = (code) => {
    setSelected((current) => {
      if (current.includes(code)) return current.filter((item) => item !== code)
      if (current.length >= 3) return current
      return [...current, code]
    })
  }

  return (
    <div className="fixed inset-0 z-[70] flex bg-dark-primary/95 p-3">
      <div className="m-auto flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 p-4">
          <div>
            <h2 className="text-xl font-black text-white">{t('achievements.editorTitle')}</h2>
            <p className="mt-1 text-xs text-text-secondary">{t('achievements.editorHint')}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white active:scale-95" aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          {[0, 1, 2].map((index) => {
            const achievement = earned.find((item) => item.code === selected[index])
            return (
              <div key={index} className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-xl border border-dashed border-white/15 bg-black/20 px-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold text-white/60">{index + 1}</span>
                {achievement ? (
                  <>
                    <img src={achievement.image} alt="" className="h-9 w-9 shrink-0 object-contain" />
                    <span className="truncate text-[11px] font-semibold text-white">{achievement.name}</span>
                  </>
                ) : (
                  <span className="truncate text-[11px] text-text-secondary">{t('achievements.emptySlot')}</span>
                )}
              </div>
            )
          })}
        </div>

        <div className="grid flex-1 grid-cols-2 gap-2 overflow-y-auto p-4 sm:grid-cols-3">
          {earned.map((achievement) => {
            const order = selected.indexOf(achievement.code)
            const tier = getTierStyle(achievement.tier)
            return (
              <button
                key={achievement.code}
                type="button"
                onClick={() => toggle(achievement.code)}
                className={`relative block rounded-xl border bg-black/20 p-2 text-center transition-all active:scale-[0.98] ${order >= 0 ? tier.border : 'border-white/10'}`}
              >
                {order >= 0 && (
                  <span className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-accent-primary text-xs font-black text-dark-primary">{order + 1}</span>
                )}
                <img src={achievement.image} alt={achievement.name} className="mx-auto h-20 w-20 object-contain" />
                <span className="mt-1 block truncate text-xs font-semibold text-white">{achievement.name}</span>
                {order >= 0 && <Check size={13} className="mx-auto mt-1 text-accent-primary" />}
              </button>
            )
          })}
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-white/10 p-4">
          <button type="button" onClick={onReset} disabled={isSaving} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 px-3 py-3 text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-50">
            <RotateCcw size={16} />
            {t('achievements.reset')}
          </button>
          <button type="button" onClick={() => onSave(selected)} disabled={selected.length === 0 || isSaving} className="gradient-border-cta flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50">
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AchievementShowcaseEditor
